import { OnDestroy, OnInit, Directive } from '@angular/core';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { OAuthData } from '../../auth/models/oauthdata.model';
import { AuthService } from '../../auth/services/auth.service';
import { IJurisdiction, Jurisdiction as JurisdictionEnum } from '../../foundation/models/enums/jurisdiction.enum';
import { appInjector } from '../../shared/app-injector';
import { RoutableLayout } from '../components/layouts/routable-layout-base.component';
import { ITemplateComm } from '../components/layouts/template-comm.interface';
import { TransactionSummaryFactory } from '../components/template/transaction-summary-factory.service';
import { Dictionary } from '../models/dictionary.model';
import { ValidationModel } from '../models/validation-results/validation.model';
import { ActionTopic } from '../services/dispatcher/action-topic.enum';
import { DispatcherService } from '../services/dispatcher/dispatcher.service';
import { HelpPanelCommunicationService } from '../services/help-panel/help-panel-communication.service';
import { RouteBuilderFactory } from '../services/route-builder/route-builder-factory.service';
import { RouterService } from '../services/router/router.service';
import { TransactionOverrideFactory } from '../services/transaction-override/transaction-override-factory.service';
import { AppSettings } from '../settings/app-settings.model';

@Directive()
export abstract class BaseComponent implements OnInit, OnDestroy, ITemplateComm {
    // Gets or sets the submitting flag when buttons are clicked
    public submitting = false;
    // Gets or sets the validationResult model
    public validationResults: ValidationModel;
    // Gets or sets the flag to show or hide the validation component
    public showValidationComponent = false;
    // Gets or sets the flag to display results
    public displayResults = false;

    protected readonly componentDestroyed: Subject<void>;

    /**
     * Gets the base jurisdiction the system is running in.
     */
    private static readonly baseJurisdiction = new JurisdictionEnum().AZ;

    // Gets or sets the Auth Service
    private _auth: AuthService;
    public template: RoutableLayout;
    private _routerService: RouterService;
    private _dispatcherService: DispatcherService;
    private _routeBuilderFactory: RouteBuilderFactory;
    private _helpPanelCommunicationService: HelpPanelCommunicationService;
    private _settings: AppSettings;
    private _transactionSummaryFactory: TransactionSummaryFactory;
    private _transactionOverrideFactory: TransactionOverrideFactory;

    /**
     * Handle the angular ngOnInit event
     */
    public abstract ngOnInit();

    constructor() {
        this.componentDestroyed = new Subject<void>();
    }

    /**
     * Handle the angular ngOnDestroy event
     */
    public ngOnDestroy(): void {
        this.componentDestroyed.next();
        this.componentDestroyed.complete();

        if (this._dispatcherService) {
            this._dispatcherService.unsubscribe(this);
        }
    }

    /**
     * Gets the dispatcher for messaging and actions for the component.
     */
    public get dispatcher(): DispatcherService {
        if (this._dispatcherService == undefined) {
            const injector = appInjector();
            if (injector != undefined) {
                this._dispatcherService = <DispatcherService> injector.get(DispatcherService);
            }
        }

        return this._dispatcherService;
    }

    /**
     * Gets the RouteBuilderFactory
     */
    public get routeBuilderFactory(): RouteBuilderFactory {
        if (this._routeBuilderFactory == undefined) {
            const injector = appInjector();
            if (injector != undefined) {
                this._routeBuilderFactory = <RouteBuilderFactory> injector.get(RouteBuilderFactory);
            }
        }

        return this._routeBuilderFactory;
    }

    public get transactionSummaryFactory(): TransactionSummaryFactory {
        if (this._transactionSummaryFactory == undefined) {
            const injector = appInjector();
            if (injector != undefined) {
                this._transactionSummaryFactory = <TransactionSummaryFactory> injector.get(TransactionSummaryFactory);
            }
        }

        return this._transactionSummaryFactory;
    }

    public get transactionOverrideFactory(): TransactionOverrideFactory {
        if (this._transactionOverrideFactory == undefined) {
            const injector = appInjector();
            if (injector != undefined) {
                this._transactionOverrideFactory = <TransactionOverrideFactory> injector.get(TransactionOverrideFactory);
            }
        }

        return this._transactionOverrideFactory;
    }

    /**
     * Sets the dispatcher for the component
     */
    public set dispatcher(value: DispatcherService) {
        this._dispatcherService = value;
    }

    /**
     * Gets the auth service that gives access to login/logout/user actions/user information
    */
    public get auth(): AuthService {
        if (this._auth == undefined) {
            const injector = appInjector();
            if (injector != undefined) {
                this._auth = <AuthService> injector.get(AuthService);
            }
        }
        return this._auth;
    }

    /**
     * Gets the router service that gives access to route information
    */
    public get routerService(): RouterService {
        if (this._routerService == undefined) {
            const injector = appInjector();
            if (injector != undefined) {
                this._routerService = <RouterService> injector.get(RouterService);
            }
        }
        return this._routerService;
    }

    /**
    * Sets the auth service for the component
    */
    public set auth(value: AuthService) {
        this._auth = value;
    }

    /**
     * The currently logged in user information
    */
    public get user(): OAuthData {
        if (this.auth != undefined) {
            return this.auth.currentUser;
        }
        return undefined;
    }

    /**
     * Clears the display of the validation results.
     */
    public clearValidation() {
        this.dispatcher.publish({ topic: ActionTopic.ValidationSuccess, values: null });
    }

    /**
     * Returns if there any validation results
     * @param validationResult
     */
    public AnyValidationResults(validationResult: any): boolean {
        this.validationResults = new ValidationModel(validationResult);

        if (this.validationResults.validationResults.length > 0) {
            return true;
        }
    }

    /**
     * Handles displaying page validation results.
     * @param validationResult The validation result object.
     */
    public handleValidationResult(validationResult: any) {
        this.validationResults = new ValidationModel(validationResult);

        if (this.validationResults.validationResults.length > 0) {
            this.showValidationComponent = true;
        }
    }

    public handleErrorMessage(errorMessage: string) {
        let validationModel = new ValidationModel([{ state: { validationType: 'Error' }, errorMessage }]);
        this.handleValidationResult(validationModel);
    }

    public clearValidationResults = (): void => {
        if ((null != this.validationResults) && (null != this.validationResults.validationResults)) {
            this.validationResults.validationResults = [];
        }
    };

    public showOverlay(overlayText: string) {
        const valDictionary: Dictionary = new Dictionary([]);
        valDictionary.add('OverlayText', overlayText);
        this.dispatcher.publish({ topic: ActionTopic.ShowOverlay, values: valDictionary });
    }

    public hideOverlay() {
        this.dispatcher.publish({ topic: ActionTopic.HideOverlay, values: null });
    }

    /**
     * Handles the failure of a call
     * @param functionName: The name of the function where the failure occurred
     * @param error: The error that occurred
     */
    public onFailure = (functionName: string, error: any): void => {
        if (error.error) {
            const valDictionary: Dictionary = new Dictionary([]);
            // const validation = JSON.parse(error._body);
            const validation = error.error;
            valDictionary.add('ValidationResults', validation);
            this.dispatcher.publish({ topic: ActionTopic.ValidationFailure, values: valDictionary });
        } else {
            const valDictionary: Dictionary = new Dictionary([]);
            valDictionary.add('ValidationResults', error);
            this.dispatcher.publish({ topic: ActionTopic.ValidationFailure, values: valDictionary });
        }

        this.dispatcher.publish({ topic: ActionTopic.ResultsReturned, values: null });
    };

    public onFailureAppend = (functionName: string, error: any): void => {
        if (error.error) {
            const valDictionary: Dictionary = new Dictionary([]);
            // const validation = JSON.parse(error._body);
            const validation = error.error;
            valDictionary.add('ValidationResults', validation);
            valDictionary.add('AppendValidationResults', true);
            this.dispatcher.publish({ topic: ActionTopic.ValidationFailure, values: valDictionary });
        } else {
            const valDictionary: Dictionary = new Dictionary([]);
            valDictionary.add('ValidationResults', error);
            valDictionary.add('AppendValidationResults', true);
            this.dispatcher.publish({ topic: ActionTopic.ValidationFailure, values: valDictionary });
        }

        this.dispatcher.publish({ topic: ActionTopic.ResultsReturned, values: null });
    };

    /**
     * Converts an IOS string date to type Date. Helpful when pulling a date out of local storage
     * @param date
     */
    public iosStringToDate(date: any): Date {
        if (typeof date === 'string') {
            const dateString: string = <string> date;
            const currentDate = new Date(dateString);
            return currentDate;
        }

        return new Date();
    }

    public get helpCommunicationService(): HelpPanelCommunicationService {
        if (this._helpPanelCommunicationService == undefined) {
            const injector = appInjector();
            if (injector != undefined) {
                this._helpPanelCommunicationService = <HelpPanelCommunicationService> injector.get(HelpPanelCommunicationService);
            }
        }

        return this._helpPanelCommunicationService;
    }

    public UseHelpContent(contentUrl: string) {
        this.helpCommunicationService.setHelpContentUrl(contentUrl);
    }

    public UseDefaultHelpContent() {
        if (this._settings == undefined) {
            const injector = appInjector();
            if (injector != undefined) {
                this._settings = <AppSettings> injector.get(AppSettings);
            }
        }

        var helpContentUrl = this._settings.help.defaultHelpContentPage;
        this.UseHelpContent(helpContentUrl);
    }

    public getAge(dateString: string, deceasedDateString: string): string {
        if (null == dateString) {
            return null;
        }

        if (deceasedDateString == null) {
            const d = moment(dateString);
            var years = Math.floor(moment().diff(d, 'years', true));
            var months = Math.floor(moment().diff(d, 'months', true));
        } else {
            var years = Math.floor(moment(deceasedDateString).diff(dateString, 'years', true));
            var months = Math.floor(moment(deceasedDateString).diff(dateString, 'months', true));
        }

        if (years > 0) {
            months = Math.floor(months % 12);
        }

        return `${years}y ${months}m`;
    }

    /**
     * get quarter number from date
     * @param date
     */
    public quarter_of_the_year(date) {
        var month = date.getMonth() + 1;
        return (Math.ceil(month / 3));
    }

    /**
     * Gets the base jurisdiction the system is running in.
     */
    public getBaseJurisdiction(): IJurisdiction {
        // We spread here to prevent the private baseJurisdiction
        // property from being mutated after we return it.
        return { ...BaseComponent.baseJurisdiction };
    }
}
